import { useEffect, useState, type ReactNode } from 'react';
import { ApiError } from '../api/client';
import { NETWORK_ERROR_MESSAGE, documentReadFailureMessage } from '../api/messages';
import { FUNNEL_STAGE_LABELS } from '../api/access';
import type { Exclusivity, LoiDecision, LoiReviewItem, LoiStatus } from '../api/types';
import { formatDate, formatDateOnly } from '../lib/date-only';
import { addCalendarDays } from '../lib/exclusivity';
import { cn } from '../lib/utils';
import { Button, buttonVariants } from './button';
import { Dialog } from './dialog';
import { PdfViewer } from './pdf-viewer';
import { PersonDisplay } from './person';
import { Chip, type ChipTone } from './status-chip';

/**
 * LOI UI shared by the admin LOI view, the seller portal's LOIs page and the
 * buyer room (ADR-0029: an LOI is an uploaded signed document with a recorded
 * status). One status vocabulary, one review table, one read-only viewer — so
 * the three apps can never describe the same LOI differently.
 */

/** The words every app shows for an LOI status. `redlined` is the stored value
 * the "counter" response records (ADR-0029); the legacy e-sign values read as
 * their plain outcome. */
export const LOI_STATUS_LABELS: Record<LoiStatus, string> = {
  submitted: 'Submitted',
  accepted: 'Accepted',
  accepted_signed: 'Accepted',
  redlined: 'Countered',
  rejected: 'Rejected',
  awaiting_buyer_signature: 'Not submitted',
};

const STATUS_TONE: Record<LoiStatus, ChipTone> = {
  submitted: 'check',
  accepted: 'certified',
  accepted_signed: 'certified',
  redlined: 'recert',
  rejected: 'error',
  awaiting_buyer_signature: 'neutral',
};

export function LoiStatusChip({ status }: { status: LoiStatus }) {
  return <Chip tone={STATUS_TONE[status]}>{LOI_STATUS_LABELS[status]}</Chip>;
}

/** An LOI whose acceptance is waiting for the owners' vote (ADR-0046):
 * the LOI itself is still `submitted`; its exclusivity is pending. */
export function isAcceptancePending(loi: Pick<LoiReviewItem, 'status' | 'exclusivity'>): boolean {
  return loi.status === 'submitted' && loi.exclusivity?.status === 'pending_validation';
}

const END_REASON_LABEL: Record<NonNullable<Exclusivity['endReason']>, string> = {
  ended_early: 'ended early',
  closed: 'closed',
  lapsed: 'ended',
  declined: 'declined by the owners',
};

/** One line describing an LOI's exclusivity for the review table. */
function exclusivitySummary(e: Exclusivity): string {
  const through = formatDateOnly(addCalendarDays(e.expiresOn, -1));
  if (e.status === 'pending_validation') return `Pending the owners’ vote · ${e.lengthDays} days`;
  if (e.status === 'active' && e.inEffect) return `In effect · through ${through}`;
  if (e.status === 'active') return `Ended · ${through}`;
  return `${e.endReason ? END_REASON_LABEL[e.endReason] : 'Over'} · through ${through}`.replace(
    /^./,
    (c) => c.toUpperCase(),
  );
}

const DECISIONS: { decision: LoiDecision; label: string; className: string }[] = [
  { decision: 'accept', label: 'Accept', className: 'text-st-certified-fg' },
  { decision: 'counter', label: 'Counter', className: 'text-st-recert-fg' },
  { decision: 'reject', label: 'Reject', className: 'text-destructive' },
];

export interface LoiReviewTableProps {
  lois: LoiReviewItem[];
  /** Whether the viewer may enter responses: an owner (while the room lets them
   * start one), or the intermediary — whose response is only a PROPOSAL the owners
   * must carry. Never in a room the buyer or the seller runs, for the admin. */
  canRespond: boolean;
  /** The LOI whose response is being recorded (its buttons are disabled). */
  decidingId: string | null;
  onDecide: (loiId: string, decision: LoiDecision) => void;
  /** Open the LOI's signed document read-only. */
  onOpen: (loi: LoiReviewItem) => void;
  /** Extra per-row controls under the actions (the admin's funnel advance). */
  renderExtra?: (loi: LoiReviewItem) => ReactNode;
  /** A "?" help tip shown after a row's status chip (return null for rows without one). */
  statusAside?: (loi: LoiReviewItem) => ReactNode;
}

/**
 * Every LOI in a room: buyer, date, status, the buyer's funnel position, the
 * signed document (opened read-only) and — on a `submitted` LOI, when the viewer
 * may respond — Accept / Counter / Reject. Recording a response is a status of
 * record; the parties paper the deal outside the app. Presentational: the page
 * owns the mutation.
 */
export function LoiReviewTable({
  lois,
  canRespond,
  decidingId,
  onDecide,
  onOpen,
  renderExtra,
  statusAside,
}: LoiReviewTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left text-muted-foreground">
            <th className="py-2 pr-4 font-medium">Buyer</th>
            <th className="py-2 pr-4 font-medium">Submitted</th>
            <th className="py-2 pr-4 font-medium">Status</th>
            <th className="py-2 pr-4 font-medium">Letter of intent</th>
            <th className="py-2 pr-4 font-medium">Funnel</th>
            <th className="py-2 pr-4 font-medium">Exclusivity / Closing</th>
            <th className="py-2 font-medium">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {lois.map((loi) => {
            const deciding = decidingId === loi.id;
            const pendingAccept = isAcceptancePending(loi);
            // An LOI with a vote open (a proposed counter / decline) takes votes, not new responses.
            const respondable =
              canRespond && loi.status === 'submitted' && !pendingAccept && !loi.sellerVote;
            const extra = renderExtra?.(loi);
            const aside = statusAside?.(loi);
            return (
              <tr key={loi.id} className="border-b align-top last:border-0">
                <td className="py-2 pr-4">
                  <PersonDisplay
                    name={loi.actorName}
                    email={loi.actorEmail}
                    fallback={loi.actorId}
                    size="sm"
                  />
                </td>
                <td className="py-2 pr-4 whitespace-nowrap">{formatDate(loi.submittedAt)}</td>
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-1.5">
                    {pendingAccept ? (
                      <Chip tone="check">Accepted — pending the owners’ vote</Chip>
                    ) : (
                      <LoiStatusChip status={loi.status} />
                    )}
                    {aside}
                  </div>
                  {loi.decidedAt ? (
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatDate(loi.decidedAt)}
                    </div>
                  ) : null}
                </td>
                <td className="py-2 pr-4">
                  {loi.documentId ? (
                    <button
                      type="button"
                      onClick={() => onOpen(loi)}
                      className="max-w-[14rem] truncate text-left text-xs font-medium text-primary hover:underline"
                      title={loi.filename ?? undefined}
                    >
                      {loi.filename ?? 'View document'}
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">No document</span>
                  )}
                </td>
                <td className="py-2 pr-4">{FUNNEL_STAGE_LABELS[loi.funnelStage]}</td>
                <td className="py-2 pr-4 text-xs">
                  {loi.exclusivity ? (
                    <div>{exclusivitySummary(loi.exclusivity)}</div>
                  ) : (
                    <div>Excl: {formatDateOnly(loi.exclusivityExpiresAt)}</div>
                  )}
                  <div>Close: {formatDateOnly(loi.closingDate)}</div>
                </td>
                <td className="py-2">
                  <div className="flex flex-col items-end gap-2">
                    {respondable ? (
                      <div className="flex gap-3" role="group" aria-label="Record a response">
                        {DECISIONS.map(({ decision, label, className }) => (
                          <button
                            key={decision}
                            type="button"
                            disabled={deciding}
                            onClick={() => onDecide(loi.id, decision)}
                            className={cn(
                              'text-xs font-medium hover:underline disabled:opacity-50',
                              className,
                            )}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    {extra}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type Loaded =
  | { kind: 'pdf'; url: string; data: ArrayBuffer }
  | { kind: 'image'; url: string }
  | { kind: 'other'; url: string };

/**
 * Read-only viewer for an LOI's signed document, fetched from the LOI document
 * path (`loiDocumentContentPath`) — never the general library, which never
 * lists an LOI. PDFs render in the in-app pdf.js viewer, images inline; any
 * other type (e.g. a Word document) offers a Download of the bytes instead.
 */
export function LoiDocumentDialog({
  contentPath,
  filename,
  onClose,
}: {
  contentPath: string;
  filename: string | null;
  onClose: () => void;
}) {
  const [content, setContent] = useState<Loaded | null>(null);
  const [error, setError] = useState<string | null>(null);
  const name = filename ?? 'Letter of intent';

  useEffect(() => {
    let objectUrl: string | null = null;
    const controller = new AbortController();
    setContent(null);
    setError(null);
    fetch(contentPath, { credentials: 'include', signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) {
          throw new ApiError(
            res.status,
            documentReadFailureMessage(res.status, 'This letter of intent is not available.'),
          );
        }
        const blob = await res.blob();
        const type = (res.headers.get('Content-Type') ?? blob.type).split(';')[0]?.trim() ?? '';
        const data = type === 'application/pdf' ? await blob.arrayBuffer() : null;
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        if (data) setContent({ kind: 'pdf', url: objectUrl, data });
        else if (type.startsWith('image/')) setContent({ kind: 'image', url: objectUrl });
        else setContent({ kind: 'other', url: objectUrl });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : NETWORK_ERROR_MESSAGE);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [contentPath]);

  const download = content ? (
    <a
      href={content.url}
      download={name}
      className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
    >
      Download
    </a>
  ) : null;

  return (
    <Dialog
      title={name}
      description="Signed letter of intent — read-only."
      onClose={onClose}
      size="xl"
      bodyClassName="p-0 sm:p-0 min-h-[60vh] flex flex-col"
      headerAside={
        <>
          {download}
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </>
      }
    >
      {error ? (
        <p className="p-6 text-sm text-destructive">{error}</p>
      ) : !content ? (
        <p className="p-6 text-sm text-muted-foreground">Loading document…</p>
      ) : content.kind === 'pdf' ? (
        <PdfViewer
          data={content.data}
          title={name}
          errorHint={`Use Download to open “${name}” on your device.`}
          className="min-h-0 flex-1"
        />
      ) : content.kind === 'image' ? (
        <div className="flex flex-1 items-center justify-center bg-muted/30 p-4">
          <img src={content.url} alt={name} className="max-h-full max-w-full object-contain" />
        </div>
      ) : (
        <div className="p-6 text-sm">
          <p className="font-medium">Preview not available for this file type.</p>
          <p className="mt-1 text-muted-foreground">
            Use Download to open “{name}” on your device.
          </p>
        </div>
      )}
    </Dialog>
  );
}
